import axios, {
  AxiosError,
  type AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { HOST_URL } from "config";
import type { CustomResponseFormat } from "api/instance";
import type { AmbassadorDashboardResult } from "api/ambassadorApi";

// 앰버서더 어드민 API — 내부(@orwellhealth.org) 구글 계정 전용.
// /user/v2 Bearer 체계를 쓰지만 기존 instancePromotion 은 재사용하지 않는다:
// 그쪽의 401 처리(/promotion-login 리다이렉트)와 전역 토큰 세팅(setBearerTokenPromotion → userSimpleAtom 동기화)이
// 어드민 세션과 프로모션 유저 세션을 서로 오염시키기 때문. 액세스 토큰은 이 모듈 메모리에만 보관하고,
// 새로고침 시에는 refresh 쿠키(httpOnly)로 조용히 재발급한다(refreshAdminToken).

let accessToken: string | null = null;
let isRefreshing = false;
let failedQueue: {
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}[] = [];

export const setAdminToken = (token: string | null) => {
  accessToken = token;
};
export const getAdminToken = () => accessToken;

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

// refresh 쿠키로 어드민 액세스 토큰 재발급. 실패 시 null (로그인 화면으로 유도).
export const refreshAdminToken = async (): Promise<string | null> => {
  try {
    const response = await axios.get(`${HOST_URL}/user/v2/token/refresh`, {
      withCredentials: true,
    });
    const newToken: string | undefined = response.data?.result?.accessToken;
    if (response.data?.code === 0 && newToken) {
      accessToken = newToken;
      return newToken;
    }
  } catch (e) {
    // 쿠키 없음/만료 — 로그인 필요
  }
  return null;
};

interface AdminInstance extends AxiosInstance {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
}

export const adminAxios: AdminInstance = axios.create({
  withCredentials: true, // 로그인 시 refresh 쿠키 수신, 재발급 시 전송
  baseURL: HOST_URL,
  timeout: 15000, // 15초 타임아웃
});

const onRequest = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
};

// 비즈니스 오류는 HTTP 200 + code≠0 이므로 envelope 를 그대로 반환한다 — code 검사는 호출부 책임.
const onResponse = (response: AxiosResponse): AxiosResponse => {
  // Blob 응답(체결본 PDF)은 Content-Disposition 의 파일명이 필요해 응답 전체를 넘긴다.
  // 나머지는 지금까지처럼 본문만 넘겨 호출처가 CodeResponse 를 그대로 받게 한다.
  if (response.data instanceof Blob) return response;
  return response.data;
};

// 401 → refresh 쿠키로 토큰 재발급 후 원 요청 재시도 (단일 refresh + 대기 큐).
// 리다이렉트는 하지 않는다 — 어드민 페이지가 실패를 받아 로그인 화면 상태로 전환한다.
const onRejected = async (error: AxiosError) => {
  const originalRequest = error.config as InternalAxiosRequestConfig & {
    _retry?: boolean;
  };
  const isUnauthorized =
    error.response?.status === 401 ||
    (error.response?.data as CustomResponseFormat | undefined)?.status === 401;

  if (
    isUnauthorized &&
    originalRequest &&
    !originalRequest._retry &&
    !originalRequest.url?.includes("/user/v2/token/refresh")
  ) {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        if (token && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        return adminAxios(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;
    const newToken = await refreshAdminToken();
    isRefreshing = false;

    if (newToken) {
      processQueue(null, newToken);
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
      }
      return adminAxios(originalRequest);
    }
    accessToken = null;
    processQueue(error);
  }
  return Promise.reject(error);
};

adminAxios.interceptors.request.use(onRequest);
adminAxios.interceptors.response.use(onResponse, onRejected);

export const REFERRAL_ADMIN_DOMAIN = "referral/admin";

export const REFERRAL_ADMIN_CODE_LIST_ENDPOINT = "code/list";
export const REFERRAL_ADMIN_CODE_CREATE_ENDPOINT = "code/create";
export const REFERRAL_ADMIN_CODE_UPDATE_ENDPOINT = "code/update";
export const REFERRAL_ADMIN_PERFORMANCE_ENDPOINT = "performance";
export const REFERRAL_ADMIN_PERFORMANCE_OVERVIEW_ENDPOINT =
  "performance/overview";
export const REFERRAL_ADMIN_SETTLEMENT_ENDPOINT = "settlement";
export const REFERRAL_ADMIN_AMBASSADOR_LIST_ENDPOINT = "ambassador/list";
export const REFERRAL_ADMIN_AMBASSADOR_CONTRACT_ENDPOINT =
  "ambassador/contract";
export const REFERRAL_ADMIN_AMBASSADOR_CONTRACT_SIGN_PDF_ENDPOINT =
  "ambassador/contract-sign-pdf";
export const REFERRAL_ADMIN_AMBASSADOR_CONTRACT_COMPLETE_ENDPOINT =
  "ambassador/contract-complete";
export const REFERRAL_ADMIN_AMBASSADOR_CONTRACT_INFO_ENDPOINT =
  "ambassador/contract-info";
export const REFERRAL_ADMIN_AMBASSADOR_GROUP_ENDPOINT = "ambassador/group";
export const REFERRAL_ADMIN_AMBASSADOR_POST_LIST_ENDPOINT =
  "ambassador/post/list";
export const REFERRAL_ADMIN_TRIAL_PREVIEW_ENDPOINT = "ambassador/trial-preview";
export const REFERRAL_ADMIN_TRIAL_SETUP_ENDPOINT = "ambassador/trial-setup";
export const REFERRAL_ADMIN_TRIAL_CANCEL_ENDPOINT = "ambassador/trial-cancel";

export type ReferralAdminCodeDetail = {
  code: string;
  name?: string | null;
  isActive: boolean;
  type: string;
  startDate?: string | null;
  endDate?: string | null;
  // 연결된 앰버서더 계정 — 표시 전용. 어드민은 계정 연결을 관리하지 않는다 (앰버서더 셀프 링크에서 붙는다).
  ambassadorPhoneNumber?: string | null;
  perPayerAmount?: number | null;
  accountStatus: string;
  // 연결된 앰버서더의 모집 차수 — 표시 전용 (수정은 앰버서더 계약 탭에서)
  ambassadorGroupNo?: number | null;
  // 연결된 앰버서더가 앰버서더 페이지에서 전자서명한 계약의 만료일(체결일+70일).
  // 외부 체결·미연결 코드는 null 이다.
  ambassadorContractEndDate?: string | null;
};

export type ReferralAdminAmbassadorContractInfoParam = {
  ambassadorId: number;
  // freelancer / business_general / business_simplified
  type: string;
  realName?: string | null;
  businessName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  bankbookFile?: string | null;
  bankbookFileName?: string | null;
  businessRegistrationFile?: string | null;
  businessRegistrationFileName?: string | null;
};

export type ReferralAdminCodeSaveParam = {
  code: string;
  name?: string | null;
  // 앰버서더는 affiliate 타입의 외부 명칭 — 어드민은 discount(단순할인) 코드도 관리한다
  type?: string | null;
  isActive?: boolean;
  startDate?: string | null;
  endDate?: string | null;
  perPayerAmount?: number | null;
};

// 성과 개요 — 기간 집계 지표. 코드별 행과 실시간 스트립이 같은 모양을 쓴다.
// revenueAmount = 기여 확정 결제(1인 1건)의 결제 금액 합 (환불분은 이미 빠져 있고, 갱신 결제는 포함하지 않는다).
// settlementAmount = 코드x월 단위 (결제자 - 환불자) x 그 달 수수료의 합. 수수료 정보가 없으면 null(미산출).
export type ReferralAdminPerformanceMetrics = {
  signupCount: number;
  payerCount: number;
  refundCount: number;
  revenueAmount: number;
  settlementAmount?: number | null;
};

// 행은 affiliate(앰버서더) 코드만이다 — discount 는 기여 기반이 아니라 성과 집계 대상이 아니다.
export type ReferralAdminPerformanceRow = {
  code: string;
  name?: string | null;
  groupNo?: number | null;
  perPayerAmount?: number | null;
  metrics: ReferralAdminPerformanceMetrics;
  // 기간 종료일까지 최근 14일의 일별 결제자 수 (오래된 날 → 최근 날)
  trend: number[];
};

// 선택 기간과 무관하게 항상 "지금" 기준. 비교값은 진행도를 맞춘 짝이다
// (오늘↔어제 하루 전체 / 이번 달↔지난달 같은 일자까지). 표와 같은 범위(앰버서더 코드 전체) 합산.
export type ReferralAdminPerformanceLive = {
  asOf: string;
  today: ReferralAdminPerformanceMetrics;
  yesterday: ReferralAdminPerformanceMetrics;
  thisMonth: ReferralAdminPerformanceMetrics;
  lastMonthToDate: ReferralAdminPerformanceMetrics;
};

// 합계는 오지 않는다 — 화면이 정렬·필터를 적용한 뒤 보이는 행으로 직접 합산한다.
export type ReferralAdminPerformanceOverviewResult = {
  from: string;
  to: string;
  rows: ReferralAdminPerformanceRow[];
  live: ReferralAdminPerformanceLive;
};

export type ReferralAdminSettlementRow = {
  code: string;
  name?: string | null;
  // 입금자명(예금주) — 지급 실무에 쓰는 값. 정산 서류를 아직 안 낸 앰버서더는 null 이고,
  // 그건 지급할 수 없다는 뜻이다.
  accountHolderName?: string | null;
  // 계좌·계약형태 등 지급 정보를 눌러서 조회하기 위한 키. 미연결 코드면 null
  ambassadorId?: number | null;
  perPayerAmount?: number | null;
  // 유효 결제 건수 — 확정 전후로 뜻이 같다 (서버가 같은 countValidPayments 로 만든다).
  payerCount: number;
  // 유효 결제에서 제외된 건수 = 결제일+7일 안에 환불이 있는 건. 확정 전에도 같은 뜻이다.
  // 그 달의 전체 환불 건수가 아니다 — 창 밖(7일 뒤) 환불은 여기에 안 잡힌다.
  // payerCount + refundCount = 그 달 기여 결제 건수.
  refundCount: number;
  settlementAmount?: number | null;
};

// confirmed: 계약서 제4조 ⑤의 확정(익월 8일)이 끝난 달인지. false 면 실시간 계산한 예상치다.
// confirmedAt / payoutDate 는 확정된 달에만 채워진다 (지급일 = 확정한 달의 10일).
export type ReferralAdminSettlementResult = {
  month: string;
  rows: ReferralAdminSettlementRow[];
  totalAmount: number;
  confirmed?: boolean;
  confirmedAt?: string | null;
  payoutDate?: string | null;
};

// 앰버서더 계정 목록 행 — 계약서 발송 실무용 (파일 제외).
// name 은 활동명(계정 단일 소스), contract* 는 계약 상태 — 계정 속성과 구분하기 위해 접두 유지.
export type ReferralAdminAmbassadorRow = {
  ambassadorId: number;
  phoneNumber?: string | null;
  verified: boolean;
  name?: string | null;
  contractType?: string | null;
  // 계약 명의 — 프리랜서는 실명, 사업자는 법인(사업자)명. 활동명(name)과 별개다.
  realName?: string | null;
  businessName?: string | null;
  contractSubmitted: boolean;
  // 계약 완료 여부 — 어드민이 체결 확인 후 토글, 완료 시 앰버서더가 셀프 링크를 만들 수 있다
  contractCompleted: boolean;
  codes: string[];
  signedUpDate?: string | null;
  // 체험 계정으로 연결된 회원번호 — 체험 설정 시 기록. 취소해도 연동은 유지된다 (구독 상태로 종료를 표기)
  trialUserId?: number | null;
  // 체험 계정의 구독 상태 (subscribing/subscribing_expired 등) — subscribing 이 아니면 "종료"로 표기
  trialUserSubscriptionStatus?: string | null;
  // 확인 대기 중인 체험용 계정 신청 — 앰버서더가 신청했고 어드민 확인(체험 설정)이 필요한 상태. 없으면 null.
  trialRequestUserId?: number | null;
  trialRequestNickname?: string | null;
  // 앰버서더 페이지 전자서명 체결 시각 (ISO) — null 이면 외부에서 체결했거나 미체결
  signedAt?: string | null;
  // 정산 서류(통장사본) 제출 여부 — 완료 처리 게이트. 구 플로우 건은 제출 시점에 항상 true
  documentsSubmitted?: boolean;
  // 모집 차수(N차) — null 이면 미지정
  groupNo?: number | null;
};

// 앰버서더가 등록한 홍보 게시물 1건. platform 은 서버가 링크 도메인에서 파생한 표기다.
export type ReferralAdminAmbassadorPostDetail = {
  postId: number;
  url: string;
  // "인스타그램" | "유튜브"
  platform: string;
  // 등록일 (yyyy-MM-dd) — 게시일이 아니라 앰버서더가 링크를 등록한 날이다
  createdDate?: string | null;
};

// 앰버서더별 게시 현황 행 — 한 행이 앰버서더 1명이다 (게시물 0건인 사람도 포함).
// 계약(체결·완료)이 있는 앰버서더만 담긴다 — 게시 의무가 없는 미인증 가입자는 서버가 제외한다.
export type ReferralAdminAmbassadorPostRow = {
  ambassadorId: number;
  // 활동명 — 링크를 만들기 전이면 null
  name?: string | null;
  // 모집 차수(N차) — null 이면 미지정, -1 은 내부자
  groupNo?: number | null;
  // 계약상 필수 건수 (서버 상수)
  requiredCount: number;
  postCount: number;
  // 가장 최근 등록일 (yyyy-MM-dd) — 등록이 없으면 null
  lastPostedDate?: string | null;
  posts: ReferralAdminAmbassadorPostDetail[];
};

// 체험 설정 적용 방식 — init: 신규 체험 시작(1개월) / extend: 이용 중 → 만료일 1개월 연장 / resume: 만료·중단 → 오늘부터 1개월 재개
export type ReferralAdminTrialMode = "init" | "extend" | "resume";

// 체험 대상 회원 미리보기 — 설정 실행 전에 적용 방식(mode)까지 서버가 판별해 준다
// 회원관리 어드민 — 서버는 /admin/user/* 에 열려 있다.
// 경로에 "/admin/" 세그먼트가 있어야 서버 JwtAuthenticationFilter 가 Bearer JWT 를 강제한다 (referral 과 같은 방식).
export const ADMIN_USER_DOMAIN = "admin/user";

export const ADMIN_USER_DETAIL_ENDPOINT = "detail";
export const ADMIN_USER_COACH_LIST_ENDPOINT = "coach/list";
export const ADMIN_USER_START_ENDPOINT = "start";
export const ADMIN_USER_RESET_ENDPOINT = "reset";
export const ADMIN_USER_REFUND_ENDPOINT = "refund";
export const ADMIN_USER_REFUND_PREVIEW_ENDPOINT = "refund/preview";
export const ADMIN_USER_EXTEND_ENDPOINT = "subscription/extend";
export const ADMIN_USER_PAUSE_ENDPOINT = "subscription/pause";
export const ADMIN_USER_PAUSE_CANCEL_ENDPOINT = "subscription/pause/cancel";
export const ADMIN_USER_RESUME_ENDPOINT = "subscription/resume";
export const ADMIN_USER_TERMINATE_ENDPOINT = "subscription/terminate";
export const ADMIN_USER_NICKNAME_ENDPOINT = "nickname";
export const ADMIN_USER_COACH_ENDPOINT = "coach";
export const ADMIN_USER_PAYBACK_REVIEW_ENDPOINT = "payback/review";
export const ADMIN_USER_PAYBACK_ENDPOINT = "payback";

export type AdminUserProfile = {
  userId: number;
  // 중복 순번이 있으면 "닉네임 (n)" 형태
  nickname?: string | null;
  // 순번을 뺀 원본 — 닉네임 변경 폼의 기본값
  rawNickname?: string | null;
  nicknameIdx: number;
  email?: string | null;
  oauthIssuer?: string | null;
  role: string;
  progressStatus: string;
  signedUpDate?: string | null;
  programStartedAt?: string | null;
  programStoppedAt?: string | null;
  programDays: number;
  programWeeks: number;
  plan?: string | null;
  planLabel?: string | null;
  programType?: string | null;
  pricingModel?: string | null;
  paymentPageType?: string | null;
  refundPolicyType?: string | null;
  promotion?: string | null;
  benefit?: string | null;
  organization?: string | null;
  isCarePass: boolean;
  isInsider: boolean;
  isStoreReviewer: boolean;
  leavedAt?: string | null;
};

export type AdminUserSubscription = {
  hasSubscription: boolean;
  status?: string | null;
  subscriptionStartedAt?: string | null;
  subscriptionExpirationDate?: string | null;
  expiryDaysLeft?: number | null;
  bonusDays?: number | null;
  canceledAt?: string | null;
  renewedAt?: string | null;
  renewalAt?: string | null;
};

export type AdminUserPauseReservation = {
  startDate: string;
  endDate: string;
  status: string;
};

export type AdminUserPause = {
  isPaused: boolean;
  pausedAt?: string | null;
  // 정지 중인데 null 이면 무기한 정지다
  resumedAt?: string | null;
  // 정지 시작일부터 오늘까지 (정지 경과)
  pausedDays?: number | null;
  // 정지 중일 때 자동 복원으로 재개하면 되는 만료일(yyyy-MM-dd, 추가 일수 0 기준). 정지 중이 아니거나 만료일이 없으면 null
  autoResumeExpirationDate?: string | null;
  totalDays: number;
  usedDays: number;
  totalCount: number;
  usedCount: number;
  reservation?: AdminUserPauseReservation | null;
  // 어드민이 미래 날짜로 잡아 둔 정지 (yyyy-MM-dd). 없으면 null. 오늘보다 앞인데 남아 있으면 그날 실행되지 못한 것이다
  scheduledStartDate?: string | null;
  // 예약이 있는데 null 이면 무기한
  scheduledEndDate?: string | null;
  // 예약한 어드민 이메일
  scheduledBy?: string | null;
  // 예약(또는 마지막으로 바꾼) 일시
  scheduledAt?: string | null;
};

// 페이백 검토 — 결제 후 100일(정지·공백 제외) 동안의 참여 사실. 날짜는 모두 yyyy-MM-dd
export type AdminUserPaybackGap = {
  from: string;
  to: string;
  // 센 날 수 (정지·공백 제외)
  days: number;
  // 구간 안에 끼어 있던 정지·공백 일수
  pausedDays: number;
};

// 기간 안에 끼어 있던 정지·공백 구간 (일시정지, 만료 뒤 다시 시작하기까지). 겹치는 구간은 서버가 합친다
export type AdminUserPaybackPause = {
  from: string;
  to: string;
  days: number;
};

// 주차는 앱의 N주차와 같다. 활동별 값은 그 활동을 한 날 수
export type AdminUserPaybackWeek = {
  week: number;
  from: string;
  to: string;
  activeDays: number;
  pausedDays: number;
  taskDays: number;
  thoughtRecordDays: number;
  meditationDays: number;
  selfCheckDays: number;
  surveyDays: number;
};

export type AdminUserPaybackThought = {
  foundAt: string;
  // 첫 영향 점수 (3-3-A). 매긴 적 없으면 null
  firstScore?: number | null;
  // 기간 안에 다시 매긴 마지막 점수. 다시 매기지 않았으면 null
  lastScore?: number | null;
};

export type AdminUserPaybackMeditation = {
  doneAt: string;
  name?: string | null;
  // 기록마다 마지막 재생 위치(초). 모르면 null — 분으로는 화면이 한 번에 반올림한다
  seconds?: number | null;
};

export type AdminUserSelfCheckPoint = {
  date: string;
  week?: number | null;
  score: number;
};

// 페이백으로 취소할 결제 한 건 — 첫 결제의 남은 금액, 또는 그 결제의 플랜 업그레이드 결제
export type AdminUserPaybackRefundItem = {
  orderId: string;
  // "첫 결제" | "플랜 업그레이드"
  label: string;
  paidAt: string;
  amount: number;
};

export type AdminUserPaybackRefund = {
  // 첫 결제 → 플랜 업그레이드 순 (취소는 서버가 최근 결제부터 한다). 페이백 버튼은 이 목록을 그대로 보낸다
  items: AdminUserPaybackRefundItem[];
  // 여기서 페이백할 수 없는 이유 (이미 페이백함·앱 스토어 결제 등). 있으면 items 는 비어 있다
  blockedReason?: string | null;
};

export type AdminUserPaybackParam = {
  // 페이백 창에 보인 결제(주문번호·금액) 그대로 — 서버가 다시 고른 대상과 하나라도 다르면 거부한다
  items: { orderId: string; amount: number }[];
  // 확인 창을 열 때 한 번 만들어 재시도에도 같은 값을 보낸다 (토스 멱등키)
  requestId: string;
  memo?: string;
};

export type AdminUserPaybackResult = {
  canceled: AdminUserPaybackRefundItem[];
  // 일부만 나간 경우 (최근 결제부터 취소하다 멈춤) — 토스 콘솔에서 사유 "페이백" 으로 마저 취소한다
  failed: AdminUserPaybackRefundItem[];
};

export type AdminUserPaybackReview = {
  windowStart: string;
  windowEnd: string;
  countedDays: number;
  pausedDays: number;
  // 정지·공백 구간, 시작일 순 — 날수를 더하면 pausedDays
  pauses: AdminUserPaybackPause[];
  isComplete: boolean;
  // 기간을 연 결제일 — 전액 환불되지 않은 첫 유료 결제
  paidAt: string;
  // 그 뒤의 추가(연장) 결제일, 오래된 순. 전액 환불된 건은 빠진다 — 연장 기간은 페이백 대상이 아니다
  extensionPaidAt: string[];
  // 신청 기한 — 100일째 날부터 10일 뒤까지. 100일을 못 채웠으면 null. 담당자 참고용 (조건 아님)
  applyUntil?: string | null;
  activeDays: number;
  gaps: AdminUserPaybackGap[];
  weeks: AdminUserPaybackWeek[];
  automaticThoughts: AdminUserPaybackThought[];
  // null 이면 명상 재생 기록을 불러오지 못한 것 (명상한 날은 weeks 에 그대로 있다)
  meditations?: AdminUserPaybackMeditation[] | null;
  depression: AdminUserSelfCheckPoint[];
  anxiety: AdminUserSelfCheckPoint[];
  // 스트레스 검사(PSS-10). 서버가 먼저 배포돼야 온다 — 없으면 기록 없음으로 본다
  stress?: AdminUserSelfCheckPoint[];
  // 페이백으로 돌려줄 결제, 또는 여기서 못 하는 이유
  refund: AdminUserPaybackRefund;
  // 스냅샷 없이 초기화된 가장 최근 날 — 그 전 활동지·명상·자동적 사고 기록은 빠져 있다
  resetWithoutSnapshotAt?: string | null;
};

export type AdminUserCoach = {
  coachId: number;
  coachName: string;
  coachDisplayName: string;
  isActive: boolean;
  coachAssignedAt?: string | null;
  assignedCoachStatus: string;
  hasChatChannel: boolean;
};

// 다운그레이드 취소 금액 안내 — 어드민은 환불을 실행하지 않고 계산만 해 준다 (취소는 토스 콘솔에서)
export type AdminUserPlanChangeHint = {
  targetPlan: string;
  targetPlanLabel: string;
  targetPrice: number;
  cancelAmount: number;
};

export type AdminUserPayment = {
  orderId: string;
  status: string;
  vendorType: string;
  productId: string;
  payAmount?: number | null;
  confirmedAt?: string | null;
  canceledAt?: string | null;
  // 누적 취소 금액. 수수료 제외 환불은 부분 취소라 결제 금액과 다르다. 컬럼 신설 전 취소 건은 null.
  canceledAmount?: number | null;
  // 토스 기준 원 결제 금액. payAmount 와 기준이 달라 취소 이력은 이 값과 짝지어 본다. 취소된 적 없으면 null.
  originalAmount?: number | null;
  promotionCode?: string | null;
  planChangeHints: AdminUserPlanChangeHint[];
  // 서버 판정. 화면이 규칙을 다시 구현하지 않는다 (paymentKey 는 안 내려온다).
  isRefundable: boolean;
};

export type AdminUserPlanUpgrade = {
  orderId: string;
  originalOrderId: string;
  status: string;
  previousPlanLabel?: string | null;
  newPlanLabel?: string | null;
  payAmount?: number | null;
  confirmedAt?: string | null;
  canceledAt?: string | null;
};

export type AdminUserAcquisitionSurvey = {
  channel: string;
  detail?: string | null;
  submittedAt?: string | null;
};

export type AdminUserWebFunnelChannel = {
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
  hasAdClickId: boolean;
  capturedAt?: string | null;
};

export type AdminUserAcquisition = {
  // 슬랙 결제 로그에 찍히는 값과 같은 규칙으로 서버가 정한다. 단서가 없으면 null.
  channelLabel?: string | null;
  survey?: AdminUserAcquisitionSurvey | null;
  webFunnel?: AdminUserWebFunnelChannel | null;
};

export type AdminUserReferral = {
  code: string;
  lastTouchedAt?: string | null;
  attributedAt?: string | null;
};

export type AdminUserActionLog = {
  actionAt?: string | null;
  adminEmail: string;
  action: string;
  beforeSummary?: string | null;
  afterSummary?: string | null;
  memo?: string | null;
};

export type AdminUserDetail = {
  user: AdminUserProfile;
  subscription: AdminUserSubscription;
  pause: AdminUserPause;
  coach?: AdminUserCoach | null;
  payments: AdminUserPayment[];
  planUpgrades: AdminUserPlanUpgrade[];
  acquisition?: AdminUserAcquisition | null;
  referral?: AdminUserReferral | null;
  actionLogs: AdminUserActionLog[];
};

export type AdminUserCoachRow = {
  coachId: number;
  name: string;
  displayName: string;
  isActive: boolean;
  isMasterPlanExcluded: boolean;
  // 자동 배정기와 같은 기준: 기준 인원(assignBasisCount) + 진행 중 내담자 수
  assignedPatientCount: number;
  assignCapCount: number;
};

// 연장·정지·재개·종료의 공통 응답 — 화면이 재조회 없이 로컬 패치한다
export type AdminUserSubscriptionResult = {
  progressStatus: string;
  subscriptionStatus?: string | null;
  subscriptionExpirationDate?: string | null;
  pausedAt?: string | null;
  resumedAt?: string | null;
  bonusDays?: number | null;
};

export type AdminUserNicknameResult = {
  nickname: string;
  chatSynced: boolean;
};

export type AdminUserCoachChangeResult = {
  coachId: number;
  coachName: string;
  announcementSent: boolean;
  warning?: string | null;
};

export type AdminUserResetResult = {
  // 값이 없던 회원은 b 로 채워진다
  programType?: string | null;
  openedTaskIds: string[];
};

// 환불 의도. 이 값이 서버가 토스에 보낼 취소 사유를 정하고, 사유가 환불 이후 회원 상태를 결정한다.
export type AdminUserRefundType = "terminate" | "amount_only" | "downgrade";

export type AdminUserRefundParam = {
  type: AdminUserRefundType;
  // 구독 종료·금액만 환불에서 필수. 다운그레이드는 서버가 계산한다.
  amount?: number;
  targetPlan?: AdminUserPlan;
  // 토스 취소 사유 뒤에 붙는 한 줄 메모. 프로그램 종료 환불에서만 쓴다 — 다운그레이드·금액만 환불은
  // 웹훅이 사유 문자열을 정확히 비교해서 한 글자만 붙어도 분기를 놓친다. 서버가 예약어도 거절한다
  cancelReasonNote?: string;
  // 팝업을 열 때 한 번 만들어 재시도에도 같은 값을 보낸다. 서버가 토스 멱등키로 써서,
  // 응답이 끊겨 다시 눌러도 돈이 두 번 나가지 않는다.
  requestId?: string;
  memo?: string;
};

export type AdminUserRefundDistributionRow = {
  orderId: string;
  cancelAmount: number;
  cancelReason: string;
};

export type AdminUserRefundPreview = {
  // 재구독 회원은 결제가 여러 건이라 서버가 가장 최근 확정 건을 잡는다
  baseOrderId: string;
  fullAmount: number;
  // 환불 정책 공제를 적용한 금액 (정책 d·e + 활동지 완료 시 90%)
  policyAmount: number;
  cancelAmount: number;
  targetPlanLabel?: string | null;
  // 이번 취소에 안 들어가는 확정 업그레이드 결제. 토스 콘솔에서 따로 처리한다
  leftoverOrderIds: string[];
  distribution: AdminUserRefundDistributionRow[];
};

export type AdminUserRefundResult = {
  cancelAmount: number;
  canceled: AdminUserRefundDistributionRow[];
};

export type AdminUserPlan = "starter" | "challenger" | "master";

export type AdminUserStartParam = {
  plan: AdminUserPlan;
  // 비우면 서버가 오늘부터 100일로 잡는다
  untilDate?: string;
  // 비우면 서버 자동 배정 규칙을 그대로 따른다
  coachId?: number;
  // 고르면 해당 혜택 표기와 Mixpanel 속성을 남긴다. 비우면 기존 혜택을 건드리지 않는다
  startOption?: AdminUserStartOption;
  memo?: string;
};

// 화면 라벨과 서버 저장값이 다르다 — 저장값은 이 옵션이 생기기 전부터 쓰던 것을 그대로 둔다
// (크리에이터 체험 = "인플루언서", 당첨 이벤트 = "인플루언서 이벤트"). 지표를 가르지 않기 위해서다.
export type AdminUserStartOption = "creator_trial" | "event_winner";

export type AdminUserStartResult = {
  progressStatus: string;
  subscriptionStatus: string;
  subscriptionExpirationDate: string;
  planLabel: string;
  // 지정하지 않았으면 자동 배정 결과다
  coachId: number;
  coachName: string;
};

export type AdminUserExtendParam = {
  // 조정할 일수 — 양수면 연장, 음수면 단축
  days?: number;
  untilDate?: string;
  fromToday?: boolean;
  memo?: string;
};

export type AdminUserResumeParam = {
  extraDays?: number;
  untilDate?: string;
  memo?: string;
};

export type ReferralAdminTrialUserPreview = {
  userId: number;
  // 중복 순번이 있으면 "닉네임 (n)" 형태
  nickname: string;
  email?: string | null;
  // 가입 방식 (oauth issuer) — kakao / google / apple 등
  oauthIssuer?: string | null;
  signedUpDate?: string | null;
  progressStatus: string;
  subscriptionStatus?: string | null;
  subscriptionExpirationDate?: string | null;
  mode: ReferralAdminTrialMode;
};

export type ReferralAdminTrialSetupResult = {
  mode: ReferralAdminTrialMode;
  subscriptionExpirationDate?: string | null;
};

// 앰버서더 계약 정보 상세 — 파일(base64 data URL) 포함.
// 계정 phoneNumber 와 계약 연락처(contractPhoneNumber)가 공존해 계약 필드는 contract* 접두 유지.
export type ReferralAdminAmbassadorContractDetail = {
  ambassadorId: number;
  phoneNumber?: string | null;
  name?: string | null;
  contractType?: string | null;
  realName?: string | null;
  businessName?: string | null;
  contractEmail?: string | null;
  contractPhoneNumber?: string | null;
  bankbookFile?: string | null;
  // 업로드 당시의 원본 파일명 — null 이면 화면이 규칙 이름으로 대체한다
  bankbookFileName?: string | null;
  businessRegistrationFile?: string | null;
  businessRegistrationFileName?: string | null;
  // 체결된 계약서 사본 — 계약 완료 처리 시 업로드
  contractFile?: string | null;
  contractFileName?: string | null;
  // 앰버서더 페이지 전자서명 체결 기록 — null 이면 외부 체결 건이거나 미체결
  sign?: ReferralAdminAmbassadorSignBlock | null;
  // 정산 정보 (전자계약 플로우의 서류 제출 단계에서 수집) — 주민번호는 원천징수 신고용으로 어드민에게만 노출
  residentRegistrationNumber?: string | null;
  bankName?: string | null;
  bankAccount?: string | null;
  accountHolderName?: string | null;
  // 예금주-실명(프리랜서)/회사명·대표자(사업자) 불일치 — 승인 전 확인이 필요한 경고
  accountHolderMismatch?: boolean;
  // 서류 제출 단계 동의 시각 — 세무 처리 확인 / 개인정보 수집·이용 (재제출 시 갱신)
  taxAgreedAt?: string | null;
  privacyAgreedAt?: string | null;
  // 연결된 링크의 수수료 — 계약 조건 확인용 (수정은 링크 관리 탭)
  codeFees?: ReferralAdminCodeFee[];
};

// 링크 1개의 수수료(유효 결제 건당 지급액). 이번 달 적용값이고, null 이면 미설정(정산 미산출).
export type ReferralAdminCodeFee = {
  code: string;
  perPayerAmount?: number | null;
};

// 전자서명 체결 기록 상세 — 체결시각·템플릿 버전·해시·요청 출처·동의(핵심 내용·전문)·체결본 전문(마크다운).
export type ReferralAdminAmbassadorSignBlock = {
  signedAt: string;
  templateVersion: string;
  snapshotHash: string;
  ip?: string | null;
  userAgent?: string | null;
  // 핵심 내용(계약 기간·수수료·유효 결제·정산·콘텐츠 의무·주의) 확인
  agreedKeyTerms: boolean;
  // 전문 확인·체결 포괄 동의 (핵심 내용 확인 후 마지막에 받는다)
  agreedFull: boolean;
  address: string;
  representativeName?: string | null;
  businessRegistrationNumber?: string | null;
  content: string;
};

// ---------- AI 관리 ----------
// 데이터셋 = 코치가 AI 출력을 쓰거나 고치거나 버린 액션 하나가 한 행 (서버 AiDatasetHelper 가 적재).
// *_json 컬럼은 서버가 파싱한 JSON 그대로 내려온다.

export const ADMIN_AI_DOMAIN = "admin/ai";

export const ADMIN_AI_DATASET_LIST_ENDPOINT = "dataset/list";
export const ADMIN_AI_DATASET_DETAIL_ENDPOINT = "dataset/detail";
export const ADMIN_AI_DATASET_UPDATE_ENDPOINT = "dataset/update";
export const ADMIN_AI_DATASET_NEW_COUNT_ENDPOINT = "dataset/new-count";
export const ADMIN_AI_DATASET_SUMMARY_ENDPOINT = "dataset/summary";
export const ADMIN_AI_DATASET_STATS_ENDPOINT = "dataset/stats";

export type AdminAiFeature =
  | "automatic_thought"
  | "chat"
  | "thought_record_comment";

export type AdminAiAction =
  | "field_save"
  | "confirm"
  | "delete"
  | "send"
  | "discard"
  | "deliver"
  | "comment_delete";

export type AdminAiLabel =
  | "as_is"
  | "edited"
  | "coach_written"
  | "cleared"
  | "unused";

export type AdminAiSplit = "train" | "valid" | "test";

// 자동적 사고는 {항목 키: 값}, 채팅·코멘트는 {text}
export type AdminAiOutput = Record<string, string | null>;

export type AdminAiDatasetRow = {
  id: number;
  feature: AdminAiFeature;
  action: AdminAiAction;
  // 자동적 사고 항목 저장일 때 항목 키
  field: string | null;
  label: AdminAiLabel;
  patientId: number;
  coachId: number | null;
  coachName: string | null;
  occurredAt: string;
  aiOutput: AdminAiOutput | null;
  coachOutput: AdminAiOutput | null;
  editRatio: number | null;
  adminEdited: boolean;
  // 학습에 쓰는지 (상세에서 켠 것)
  sftEnabled: boolean;
  dpoEnabled: boolean;
  rlEnabled: boolean;
  split: AdminAiSplit;
  // live / backfill (backfill 은 입력 스냅샷이 추정치)
  origin: string;
};

// 최신순 커서 페이징 — 다음 페이지는 이 페이지 마지막 행의 occurredAt·id 를 커서로 준다
export type AdminAiDatasetListResult = {
  rows: AdminAiDatasetRow[];
  hasNext: boolean;
  // 필터에 걸린 전체 행 수 — 첫 페이지(커서 없음)에서만 온다
  totalCount: number | null;
};

// 영역별 건수·요약 (영역 외 필터 무관)
export type AdminAiDatasetSummaryResult = {
  featureCounts: Record<string, number>;
  summary: { totalCount: number; todayCount: number; adminEditedCount: number };
};

// 라벨 통계 — 목록 필터(라벨 제외) 기준. 자동적 사고 항목 저장 행은 빼고(확정 행과 중복), AI 출력이 있던 행만 센다
export type AdminAiDatasetStatsResult = {
  // 채택률·수정률·미사용률의 분모
  aiOfferedCount: number;
  asIsCount: number;
  editedCount: number;
  unusedCount: number;
  // 수정 행의 평균 편집 비율 (0~1). 수정 행이 없으면 null
  editedAvgRatio: number | null;
  // 그대로가 아니고 코치 값이 있는 행 (AI 출력 = rejected, 코치 값 = chosen)
  dpoCandidateCount: number;
  // 학습 준비 — 필터에 걸린 전체 행 기준 (항목 저장 행·AI 출력 없는 행 포함)
  filteredCount: number;
  sftCount: number;
  dpoCount: number;
  rlCount: number;
  // 코치 값도 관리자 보완도 없어 학습 샘플이 안 만들어지는 행
  noAnswerCount: number;
};

export type AdminAiDatasetFilterParam = {
  // 여러 개를 고를 수 있다 (쉼표로 보낸다). 없으면 전 영역
  feature?: AdminAiFeature | AdminAiFeature[];
  action?: AdminAiAction;
  label?: AdminAiLabel;
  coachId?: number;
  adminEdited?: boolean;
  // yyyy-MM-dd, 양끝 포함
  from?: string;
  to?: string;
};

export type AdminAiDatasetListParam = AdminAiDatasetFilterParam & {
  // 직전 페이지 마지막 행 (둘 다 주거나 둘 다 뺀다). 없으면 첫 페이지
  cursorOccurredAt?: string;
  cursorId?: number;
  size: number;
};

export type AdminAiDatasetGroupRow = {
  id: number;
  action: AdminAiAction;
  field: string | null;
  label: AdminAiLabel;
  occurredAt: string;
};

export type AdminAiDatasetDetail = {
  id: number;
  feature: AdminAiFeature;
  action: AdminAiAction;
  field: string | null;
  sourceKey: string;
  groupKey: string | null;
  patientId: number;
  coachId: number | null;
  coachName: string | null;
  thoughtRecordKey: string | null;
  label: AdminAiLabel;
  // 자동적 사고 확정 행의 항목별 라벨
  fieldLabels: Record<string, AdminAiLabel> | null;
  // 자동적 사고 {activity_content: string[]}, 채팅 {patient_message}, 코멘트 {location, comment_type, thought_record_data}
  input: Record<string, unknown> | null;
  aiOutput: AdminAiOutput | null;
  coachOutput: AdminAiOutput | null;
  // null 이면 코치 값이 학습 정답
  adminOutput: AdminAiOutput | null;
  editRatio: number | null;
  split: AdminAiSplit;
  origin: string;
  occurredAt: string;
  sftEnabled: boolean;
  dpoEnabled: boolean;
  rlEnabled: boolean;
  trainingConfig: Record<string, unknown> | null;
  memo: string | null;
  adminUpdatedBy: string | null;
  adminUpdatedAt: string | null;
  // 같은 원본(group_key)에서 나온 행들, 시간순 — 자기 자신 포함
  groupRows: AdminAiDatasetGroupRow[];
};

// 모달 폼 전체 상태 저장 (null = 비움). 적재된 원본 값은 서버가 바꾸지 않는다.
export type AdminAiDatasetUpdateParam = {
  id: number;
  adminOutput: AdminAiOutput | null;
  sftEnabled: boolean;
  dpoEnabled: boolean;
  rlEnabled: boolean;
  split: AdminAiSplit;
  trainingConfig: Record<string, unknown> | null;
  memo: string | null;
};

// undefined·빈 문자열·빈 배열 값은 쿼리에서 빼고, 배열은 쉼표로 잇는다 (서버 @RequestParam List 가 쉼표로 나눈다)
const definedParams = (param: object) =>
  Object.fromEntries(
    Object.entries(param)
      .map(([key, value]) => [
        key,
        Array.isArray(value) ? value.join(",") : value,
      ])
      .filter(([, value]) => value !== undefined && value !== "")
  );

export const adminApi = {
  // 구글 credential(id_token)로 로그인 — 퍼널 훅(useLoginPromotion)의 부수효과 없이 토큰만 발급받는다
  login: (idToken: string) =>
    adminAxios.post<CustomResponseFormat<{ accessToken: string }>>(
      "user/v2/login",
      { type: "google", idToken }
    ),

  // 권한 없는 계정으로 들어왔을 때 다른 계정으로 갈아타기 — 서버가 refresh 쿠키를 지운다. 액세스 토큰은 호출처가 비운다.
  logout: () => adminAxios.post<CustomResponseFormat<void>>("user/v2/logout"),

  codeList: () =>
    adminAxios.get<CustomResponseFormat<ReferralAdminCodeDetail[]>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_CODE_LIST_ENDPOINT}`
    ),

  codeCreate: (param: ReferralAdminCodeSaveParam) =>
    adminAxios.post<CustomResponseFormat<void>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_CODE_CREATE_ENDPOINT}`,
      param
    ),

  codeUpdate: (param: ReferralAdminCodeSaveParam) =>
    adminAxios.post<CustomResponseFormat<void>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_CODE_UPDATE_ENDPOINT}`,
      param
    ),

  performance: (code: string) =>
    adminAxios.get<CustomResponseFormat<AmbassadorDashboardResult>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_PERFORMANCE_ENDPOINT}`,
      {
        params: { code },
      }
    ),

  // 성과 개요 — 기간(from~to, 날짜 포함) 내 코드별 집계 + 실시간 스트립. 화면이 주기적으로 다시 호출한다.
  performanceOverview: (from: string, to: string) =>
    adminAxios.get<
      CustomResponseFormat<ReferralAdminPerformanceOverviewResult>
    >(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_PERFORMANCE_OVERVIEW_ENDPOINT}`,
      {
        params: { from, to },
      }
    ),

  settlement: (month: string) =>
    adminAxios.get<CustomResponseFormat<ReferralAdminSettlementResult>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_SETTLEMENT_ENDPOINT}`,
      {
        params: { month },
      }
    ),

  ambassadorList: () =>
    adminAxios.get<CustomResponseFormat<ReferralAdminAmbassadorRow[]>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_AMBASSADOR_LIST_ENDPOINT}`
    ),

  // 앰버서더별 게시 현황 — 필수 건수 대비 등록 건수와 게시물 링크 (조회 전용)
  ambassadorPostList: () =>
    adminAxios.get<CustomResponseFormat<ReferralAdminAmbassadorPostRow[]>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_AMBASSADOR_POST_LIST_ENDPOINT}`
    ),

  ambassadorContract: (ambassadorId: number) =>
    adminAxios.get<CustomResponseFormat<ReferralAdminAmbassadorContractDetail>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_AMBASSADOR_CONTRACT_ENDPOINT}`,
      {
        params: { ambassadorId },
      }
    ),

  // 체결본 PDF 다운로드 — 서버가 스냅샷 전문을 계약서 양식(A4)으로 렌더링한다.
  // 정상 응답은 PDF Blob, 미체결 등 비즈니스 오류는 JSON Blob 으로 온다 — type 검사는 호출부 책임.
  // 응답 전체를 돌려준다 — 다운로드 파일명(Content-Disposition)을 호출처가 읽어야 한다.
  ambassadorContractSignPdf: (ambassadorId: number) =>
    adminAxios.get<AxiosResponse<Blob>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_AMBASSADOR_CONTRACT_SIGN_PDF_ENDPOINT}`,
      { params: { ambassadorId }, responseType: "blob" }
    ),

  // 계약 완료 토글 — 완료 시 앰버서더가 셀프 링크를 만들 수 있다.
  // 완료 처리 시 체결된 계약서 사본(base64 data URL)을 함께 보관할 수 있다 (해제해도 사본은 유지).
  ambassadorContractComplete: (
    ambassadorId: number,
    completed: boolean,
    contractFile?: string | null,
    contractFileName?: string | null
  ) =>
    adminAxios.post<CustomResponseFormat<void>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_AMBASSADOR_CONTRACT_COMPLETE_ENDPOINT}`,
      {
        ambassadorId,
        completed,
        contractFile: contractFile ?? null,
        contractFileName: contractFileName ?? null,
      }
    ),

  // 계약 정보 수기 입력·수정 — 앰버서더가 직접 내지 않은 건을 운영이 대신 채운다.
  // 전달한 항목만 덮어쓴다 (undefined = 안 바꿈). 계약 행이 없으면 서버가 만든다.
  ambassadorContractInfo: (param: ReferralAdminAmbassadorContractInfoParam) =>
    adminAxios.post<CustomResponseFormat<void>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_AMBASSADOR_CONTRACT_INFO_ENDPOINT}`,
      param
    ),

  // 모집 차수(N차) 지정 — null 이면 미지정으로 되돌린다
  ambassadorGroup: (ambassadorId: number, groupNo: number | null) =>
    adminAxios.post<CustomResponseFormat<void>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_AMBASSADOR_GROUP_ENDPOINT}`,
      { ambassadorId, groupNo }
    ),

  // 체험 대상 회원 미리보기 — 가입일/닉네임(순번 포함)/이용 상태 + 적용 방식(mode)
  trialPreview: (userId: number) =>
    adminAxios.get<CustomResponseFormat<ReferralAdminTrialUserPreview>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_TRIAL_PREVIEW_ENDPOINT}`,
      { params: { userId } }
    ),

  // 체험 설정 실행 — 신규 회원은 인플루언서 체험 시작(100일), 이용 중 회원은 지정 일수만큼 연장(days, 미전달 시 100일),
  // 만료·종료 회원은 재개(100일)
  trialSetup: (ambassadorId: number, userId: number, days?: number) =>
    adminAxios.post<CustomResponseFormat<ReferralAdminTrialSetupResult>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_TRIAL_SETUP_ENDPOINT}`,
      { ambassadorId, userId, ...(days !== undefined && { days }) }
    ),

  // 체험 취소 — 세팅된 체험 계정의 구독을 즉시 종료한다 (연동 자체는 유지, 목록에 "종료"로 표기)
  trialCancel: (ambassadorId: number) =>
    adminAxios.post<CustomResponseFormat<void>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_TRIAL_CANCEL_ENDPOINT}`,
      { ambassadorId }
    ),

  // 회원 상세 — 프로필/구독/일시정지/코치/결제 이력/처리 이력을 한 번에 받는다
  userDetail: (userId: number) =>
    adminAxios.get<CustomResponseFormat<AdminUserDetail>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_DETAIL_ENDPOINT}`,
      { params: { userId } }
    ),

  // 페이백 검토 — 결제 이력이 없는 회원은 서버가 거부한다 (133)
  userPaybackReview: (userId: number) =>
    adminAxios.get<CustomResponseFormat<AdminUserPaybackReview>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_PAYBACK_REVIEW_ENDPOINT}`,
      { params: { userId } }
    ),

  // 페이백 실행 — 첫 결제(업그레이드 포함) 전액을 토스에서 취소한다. 이용 중이던 회원의 종료는 토스 웹훅이 몇 초 뒤에 한다.
  userPayback: (userId: number, param: AdminUserPaybackParam) =>
    adminAxios.post<CustomResponseFormat<AdminUserPaybackResult>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_PAYBACK_ENDPOINT}`,
      {
        userId,
        items: param.items,
        requestId: param.requestId,
        memo: param.memo ?? null,
      }
    ),

  // 코치 변경 드롭다운용 — 재직 코치 + 담당 인원 수
  userCoachList: () =>
    adminAxios.get<CustomResponseFormat<AdminUserCoachRow[]>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_COACH_LIST_ENDPOINT}`
    ),

  // 환불 미리보기 — 토스를 호출하지 않고 금액과 취소 사유만 계산한다.
  userRefundPreview: (userId: number, param: AdminUserRefundParam) =>
    adminAxios.post<CustomResponseFormat<AdminUserRefundPreview>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_REFUND_PREVIEW_ENDPOINT}`,
      {
        userId,
        type: param.type,
        ...(param.amount !== undefined && { amount: param.amount }),
        ...(param.targetPlan !== undefined && { targetPlan: param.targetPlan }),
        ...(param.cancelReasonNote !== undefined && {
          cancelReasonNote: param.cancelReasonNote,
        }),
      }
    ),

  // 환불 실행 — 토스에 취소만 건다. 구독·플랜 반영은 토스 웹훅이 몇 초 뒤에 한다.
  userRefund: (userId: number, param: AdminUserRefundParam) =>
    adminAxios.post<CustomResponseFormat<AdminUserRefundResult>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_REFUND_ENDPOINT}`,
      {
        userId,
        type: param.type,
        ...(param.amount !== undefined && { amount: param.amount }),
        ...(param.targetPlan !== undefined && { targetPlan: param.targetPlan }),
        ...(param.cancelReasonNote !== undefined && {
          cancelReasonNote: param.cancelReasonNote,
        }),
        ...(param.requestId !== undefined && { requestId: param.requestId }),
        memo: param.memo ?? null,
      }
    ),

  // 프로그램 초기화 — 활동지·기록을 지우고 초기 활동지를 다시 연다. 구독·코치는 그대로다.
  userReset: (userId: number, memo?: string) =>
    adminAxios.post<CustomResponseFormat<AdminUserResetResult>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_RESET_ENDPOINT}`,
      { userId, memo: memo ?? null }
    ),

  // 프로그램 시작 — 준비 중인 회원 전용. untilDate·coachId 는 비우면 서버 기본값(100일 / 자동 배정)을 쓴다.
  userStart: (userId: number, param: AdminUserStartParam) =>
    adminAxios.post<CustomResponseFormat<AdminUserStartResult>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_START_ENDPOINT}`,
      {
        userId,
        plan: param.plan,
        ...(param.untilDate !== undefined && { untilDate: param.untilDate }),
        ...(param.coachId !== undefined && { coachId: param.coachId }),
        ...(param.startOption !== undefined && {
          startOption: param.startOption,
        }),
        memo: param.memo ?? null,
      }
    ),

  // 기한 조정 — days(음수면 단축) 또는 untilDate 중 하나만 보낸다. 보내지 않은 키는 아예 싣지 않는다.
  userExtend: (userId: number, param: AdminUserExtendParam) =>
    adminAxios.post<CustomResponseFormat<AdminUserSubscriptionResult>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_EXTEND_ENDPOINT}`,
      {
        userId,
        ...(param.days !== undefined && { days: param.days }),
        ...(param.untilDate !== undefined && { untilDate: param.untilDate }),
        ...(param.fromToday !== undefined && { fromToday: param.fromToday }),
        memo: param.memo ?? null,
      }
    ),

  // 일시정지 — endDate 가 null 이면 무기한 (재개는 어드민이 직접 해야 한다).
  // startDate 는 미래 날짜일 때만 싣는다 — 없으면 서버가 오늘 바로 정지한다 (미래 날짜면 그날 아침으로 예약).
  userPause: (
    userId: number,
    endDate: string | null,
    memo?: string,
    startDate?: string
  ) =>
    adminAxios.post<CustomResponseFormat<AdminUserSubscriptionResult>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_PAUSE_ENDPOINT}`,
      {
        userId,
        endDate,
        ...(startDate !== undefined && { startDate }),
        memo: memo ?? null,
      }
    ),

  // 미래 날짜로 잡아 둔 정지 예약을 지운다. 이용 상태·기한은 예약 때 바뀌지 않아 그대로다.
  userPauseCancel: (userId: number, memo?: string) =>
    adminAxios.post<CustomResponseFormat<AdminUserSubscriptionResult>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_PAUSE_CANCEL_ENDPOINT}`,
      { userId, memo: memo ?? null }
    ),

  // 일시정지 종료 — 기본은 정지 일수 자동 복원. untilDate 를 주면 만료일을 그대로 쓴다.
  userResume: (userId: number, param: AdminUserResumeParam) =>
    adminAxios.post<CustomResponseFormat<AdminUserSubscriptionResult>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_RESUME_ENDPOINT}`,
      {
        userId,
        ...(param.extraDays !== undefined && { extraDays: param.extraDays }),
        ...(param.untilDate !== undefined && { untilDate: param.untilDate }),
        memo: param.memo ?? null,
      }
    ),

  userTerminate: (userId: number, memo?: string) =>
    adminAxios.post<CustomResponseFormat<AdminUserSubscriptionResult>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_TERMINATE_ENDPOINT}`,
      { userId, memo: memo ?? null }
    ),

  userNickname: (userId: number, nickname: string, memo?: string) =>
    adminAxios.post<CustomResponseFormat<AdminUserNicknameResult>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_NICKNAME_ENDPOINT}`,
      { userId, nickname, memo: memo ?? null }
    ),

  userCoachChange: (
    userId: number,
    coachUserId: number,
    sendAnnouncement: boolean,
    memo?: string
  ) =>
    adminAxios.post<CustomResponseFormat<AdminUserCoachChangeResult>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_COACH_ENDPOINT}`,
      { userId, coachUserId, sendAnnouncement, memo: memo ?? null }
    ),

  // ---------- AI 관리 ----------

  // 비어 있는 필터는 쿼리에서 뺀다 — 서버는 없는 조건을 "전체"로 본다
  aiDatasetList: (param: AdminAiDatasetListParam) =>
    adminAxios.get<CustomResponseFormat<AdminAiDatasetListResult>>(
      `${ADMIN_AI_DOMAIN}/${ADMIN_AI_DATASET_LIST_ENDPOINT}`,
      { params: definedParams(param) }
    ),

  // "새 행 N건" 폴링 — 목록과 같은 조건에서 afterId 보다 id 가 큰 행 수
  aiDatasetNewCount: (param: AdminAiDatasetFilterParam & { afterId: number }) =>
    adminAxios.get<CustomResponseFormat<number>>(
      `${ADMIN_AI_DOMAIN}/${ADMIN_AI_DATASET_NEW_COUNT_ENDPOINT}`,
      { params: definedParams(param) }
    ),

  // feature 가 없으면 전 영역 요약
  aiDatasetSummary: (feature?: AdminAiFeature) =>
    adminAxios.get<CustomResponseFormat<AdminAiDatasetSummaryResult>>(
      `${ADMIN_AI_DOMAIN}/${ADMIN_AI_DATASET_SUMMARY_ENDPOINT}`,
      { params: definedParams({ feature }) }
    ),

  aiDatasetStats: (
    param: Omit<AdminAiDatasetFilterParam, "action" | "label">
  ) =>
    adminAxios.get<CustomResponseFormat<AdminAiDatasetStatsResult>>(
      `${ADMIN_AI_DOMAIN}/${ADMIN_AI_DATASET_STATS_ENDPOINT}`,
      { params: definedParams(param) }
    ),

  aiDatasetDetail: (id: number) =>
    adminAxios.get<CustomResponseFormat<AdminAiDatasetDetail>>(
      `${ADMIN_AI_DOMAIN}/${ADMIN_AI_DATASET_DETAIL_ENDPOINT}`,
      { params: { id } }
    ),

  aiDatasetUpdate: (param: AdminAiDatasetUpdateParam) =>
    adminAxios.post<CustomResponseFormat<AdminAiDatasetDetail>>(
      `${ADMIN_AI_DOMAIN}/${ADMIN_AI_DATASET_UPDATE_ENDPOINT}`,
      param
    ),
};
